import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { apiClient, EventSchedule, EKYCStatusResponse, EKYCApplicationResponse } from '../lib/api-client';
import type { StreamerProfileUpdate } from '../lib/types';
import { ROUTES } from '../lib/constants';
import { resolveMediaUrl } from '../lib/media-url';
import {
    PROFILE_IMAGE_REVIEW_MESSAGES,
    profileImageReviewState,
    profileSaveErrorMessage,
    type ProfileImageReviewState,
} from '../lib/moderation-messages';

import StreamerHeader from '../components/StreamerHeader';
import { EKYCModal } from '../components/eKYC';

export default function StreamerMyPage() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [imageReview, setImageReview] = useState<ProfileImageReviewState>(null);
    const [imageCacheBuster, setImageCacheBuster] = useState<number>(Date.now());
    const [isLoadingProfile, setIsLoadingProfile] = useState(true);
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState(false);

    // プロフィールフォームのステート
    const [displayName, setDisplayName] = useState('');
    const [age, setAge] = useState('');
    const [height, setHeight] = useState('');
    const [hobbies, setHobbies] = useState('');
    const [favoriteFood, setFavoriteFood] = useState('');
    const [features, setFeatures] = useState('');
    const [bio, setBio] = useState('');

    // 配信スケジュールのステート
    const [schedules, setSchedules] = useState<EventSchedule[]>([]);
    const [isLoadingSchedules, setIsLoadingSchedules] = useState(true);

    // 本人確認（eKYC）のステート
    const [isEKYCModalOpen, setIsEKYCModalOpen] = useState(false);
    const [verificationStatus, setVerificationStatus] = useState<EKYCStatusResponse | null>(null);
    const [isLoadingVerification, setIsLoadingVerification] = useState(true);
    const { t } = useTranslation();

    // プロフィールを取得
    useEffect(() => {
        const fetchProfile = async () => {
            if (user?.username) {
                setIsLoadingProfile(true);
                try {
                    const profile = await apiClient.getStreamerProfile(user.username);
                    if (profile.profile_image_url) {
                        // バックエンドのURLを補完
                        const imageUrl = resolveMediaUrl(profile.profile_image_url);
                        setProfileImageUrl(imageUrl);
                    }
                    // フォームの初期値を設定
                    setDisplayName(profile.display_name || user.username || '');
                    setAge(profile.age ? String(profile.age) : '');
                    setHeight(profile.height ? String(profile.height) : '');
                    setHobbies(profile.hobbies || '');
                    setFavoriteFood(profile.favorite_food || '');
                    setFeatures(profile.features || '');
                    setBio(profile.bio || '');
                } catch (error) {
                    console.error('Failed to fetch profile:', error);
                    // エラー時はデフォルト値を設定
                    setDisplayName(user.username || '');
                } finally {
                    setIsLoadingProfile(false);
                }
            } else {
                setIsLoadingProfile(false);
            }
        };
        fetchProfile();
    }, [user?.username]);

    // プロフィール画像の承認状態（承認待ち・非承認）
    useEffect(() => {
        if (!user?.username) return;
        apiClient.getMyModerationStatus()
            .then((status) => setImageReview(profileImageReviewState(status)))
            .catch((error) => console.error('Failed to fetch moderation status:', error));
    }, [user?.username]);

    // 配信スケジュールを取得
    useEffect(() => {
        const fetchSchedules = async () => {
            if (user?.id) {
                setIsLoadingSchedules(true);
                try {
                    const response = await apiClient.listEventSchedules({
                        streamer_id: user.id,
                        upcoming_only: true,
                        limit: 10,
                    });
                    setSchedules(response.schedules || []);
                } catch (error) {
                    console.error('Failed to fetch schedules:', error);
                    setSchedules([]);
                } finally {
                    setIsLoadingSchedules(false);
                }
            } else {
                setIsLoadingSchedules(false);
            }
        };
        fetchSchedules();
    }, [user?.id]);

    // 本人確認状態を取得
    useEffect(() => {
        const fetchVerificationStatus = async () => {
            setIsLoadingVerification(true);
            try {
                const status = await apiClient.getMyEKYCStatus();
                setVerificationStatus(status);
            } catch (error) {
                console.error('Failed to fetch verification status:', error);
                setVerificationStatus(null);
            } finally {
                setIsLoadingVerification(false);
            }
        };
        fetchVerificationStatus();
    }, []);

    // 本人確認完了後のコールバック
    const handleVerificationComplete = async (_result: EKYCApplicationResponse) => {
        // 状態を再取得
        try {
            const status = await apiClient.getMyEKYCStatus();
            setVerificationStatus(status);
        } catch (error) {
            console.error('Failed to refresh verification status:', error);
        }
        setIsEKYCModalOpen(false);
    };

    // 本人確認ステータスに応じた表示を取得
    const getVerificationStatusDisplay = () => {
        if (isLoadingVerification) {
            return { color: 'bg-gray-100 text-gray-600', text: '読み込み中...' };
        }
        if (!verificationStatus) {
            return { color: 'bg-gray-100 text-gray-600', text: t('ekyc.status.unverified', '未確認') };
        }
        switch (verificationStatus.status) {
            case 'APPROVED':
                return { color: 'bg-green-100 text-green-800', text: t('ekyc.status.approved', '確認済み') };
            case 'SUBMITTED':
            case 'API_VERIFYING':
                return { color: 'bg-blue-100 text-blue-800', text: t('ekyc.status.api_verifying', '本人確認中') };
            case 'PENDING_REVIEW':
                return { color: 'bg-yellow-100 text-yellow-800', text: t('ekyc.status.pending', '審査中') };
            case 'REJECTED':
                return { color: 'bg-red-100 text-red-800', text: t('ekyc.status.rejected', '却下') };
            case 'EXPIRED':
                return { color: 'bg-yellow-100 text-yellow-800', text: t('ekyc.status.expired', '期限切れ') };
            default:
                return { color: 'bg-gray-100 text-gray-600', text: t('ekyc.status.unverified', '未確認') };
        }
    };

    // プロフィールを保存
    const handleSaveProfile = async () => {
        if (!user?.username) return;

        setIsSaving(true);
        setSaveError(null);
        setSaveSuccess(false);

        try {
            // 数値に変換
            const ageNum = age ? parseInt(age, 10) : undefined;
            const heightNum = height ? parseInt(height, 10) : undefined;

            const updateData: StreamerProfileUpdate = {
                display_name: displayName || undefined,
                age: ageNum && !isNaN(ageNum) ? ageNum : undefined,
                height: heightNum && !isNaN(heightNum) ? heightNum : undefined,
                hobbies: hobbies || undefined,
                favorite_food: favoriteFood || undefined,
                features: features || undefined,
                bio: bio || undefined,
            };

            await apiClient.updateStreamerProfile(user.username, updateData);
            setSaveSuccess(true);
            // 3秒後に成功メッセージを消す
            setTimeout(() => setSaveSuccess(false), 3000);
        } catch (error) {
            console.error('Failed to save profile:', error);
            // エラーの詳細を表示（NGワードの場合は項目と語句を示す）
            setSaveError(profileSaveErrorMessage(error));
        } finally {
            setIsSaving(false);
        }
    };

    const handleLogout = async () => {
        // Stop any active streams before logout
        if (user?.id) {
            try {
                const response = await apiClient.getActiveStreams();
                const myStreams = response.streams.filter(s => s.user_id === user.id);
                for (const stream of myStreams) {
                    await apiClient.stopStream({ stream_key: stream.stream_key });
                }
            } catch (err) {
                console.error('Failed to stop streams on logout:', err);
            }
        }

        logout();
        window.location.href = ROUTES.STAFF_LOGIN;
    };

    const handleImageClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // ファイルサイズチェック (5MB)
        if (file.size > 5 * 1024 * 1024) {
            setUploadError('ファイルサイズは5MB以下にしてください');
            return;
        }

        // ファイル形式チェック
        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
        if (!allowedTypes.includes(file.type)) {
            setUploadError('JPG、PNG、WebP、GIF形式のみ対応しています');
            return;
        }

        // プレビュー表示
        const reader = new FileReader();
        reader.onload = (e) => {
            setPreviewUrl(e.target?.result as string);
        };
        reader.readAsDataURL(file);

        // アップロード
        setIsUploading(true);
        setUploadError(null);

        try {
            // 画像は運営の承認後に公開される。表示は承認済みの画像のまま
            const response = await apiClient.uploadProfileImage(file);
            setProfileImageUrl(response.profile_image_url ? resolveMediaUrl(response.profile_image_url) : null);
            setImageCacheBuster(Date.now()); // キャッシュバスター更新
            setPreviewUrl(null);
            setImageReview('pending');
        } catch (error) {
            console.error('Upload failed:', error);
            setUploadError('アップロードに失敗しました');
            setPreviewUrl(null);
        } finally {
            setIsUploading(false);
            // input をリセット（同じファイルを再選択可能にする）
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    // キャッシュバスターを追加してブラウザキャッシュをバイパス
    const getImageUrlWithCacheBuster = (url: string | null) => {
        if (!url) return null; // アイコン表示に変更
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}t=${imageCacheBuster}`;
    };
    const displayImageUrl = previewUrl || getImageUrlWithCacheBuster(profileImageUrl);

    // デフォルトのユーザーアイコン（SVG）
    const DefaultUserIcon = ({ className }: { className?: string }) => (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
        </svg>
    );

    // 配信スケジュールの日時フォーマット
    const formatScheduleDate = (startStr: string, endStr: string) => {
        const start = new Date(startStr);
        const end = new Date(endStr);
        const now = new Date();

        const isToday = start.toDateString() === now.toDateString();
        const isTomorrow = start.toDateString() === new Date(now.getTime() + 24 * 60 * 60 * 1000).toDateString();

        const timeFormat = (date: Date) => {
            return `${date.getHours()}:${date.getMinutes().toString().padStart(2, '0')}`;
        };

        const datePrefix = isToday ? '本日' : isTomorrow ? '明日' : `${start.getMonth() + 1}/${start.getDate()}`;
        return `${datePrefix} ${timeFormat(start)}-${timeFormat(end)}`;
    };

    // 配信開始までの時間を計算
    const getTimeUntilStart = (startStr: string) => {
        const start = new Date(startStr);
        const now = new Date();
        const diffMs = start.getTime() - now.getTime();

        if (diffMs <= 0) return null;

        const diffMins = Math.floor(diffMs / (1000 * 60));
        if (diffMins < 60) return `配信開始${diffMins}分前`;
        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24) return `配信開始${diffHours}時間前`;
        const diffDays = Math.floor(diffHours / 24);
        return `配信開始${diffDays}日前`;
    };

    return (
        <div className="min-h-screen bg-gray-100 font-sans text-gray-800">
            {/* Header Navigation */}
            <StreamerHeader
                activeTab="mypage"
                onMyPageClick={() => {
                    // Already on mypage
                }}
                onStudioClick={() => navigate(ROUTES.STUDIO)}
                onLogout={handleLogout}
                username={user?.username}
            />

            {/* Main Container */}
            <div className="max-w-[1000px] mx-auto my-8 px-4">
                {/* Profile Card */}
                <div className="bg-white rounded-xl p-8 mb-8 shadow-sm">
                    <div className="flex gap-8 items-start">
                        {/* プロフィール画像（クリックで変更） */}
                        <div className="flex flex-col items-center">
                            <div className="relative group">
                                <div
                                    className="w-[120px] h-[120px] rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden flex-shrink-0 cursor-pointer"
                                    onClick={handleImageClick}
                                >
                                    {isUploading || isLoadingProfile ? (
                                        <div className="w-full h-full flex items-center justify-center bg-gray-200 animate-pulse">
                                            {isUploading && (
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
                                            )}
                                        </div>
                                    ) : displayImageUrl ? (
                                        <img
                                            src={displayImageUrl}
                                            alt={user?.username || 'プロフィール'}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center bg-gray-200">
                                            <DefaultUserIcon className="w-16 h-16 text-gray-400" />
                                        </div>
                                    )}
                                </div>
                                {/* ホバー時のオーバーレイ */}
                                <div
                                    className="absolute inset-0 rounded-lg bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                                    onClick={handleImageClick}
                                >
                                    <span className="text-white text-xs font-semibold">変更</span>
                                </div>
                                {/* 隠しファイル入力 */}
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp,image/gif"
                                    onChange={handleFileSelect}
                                    className="hidden"
                                />
                            </div>
                            <p className="text-xs text-gray-500 mt-2">クリックして変更</p>
                            {imageReview && (
                                <p
                                    className={`mt-2 max-w-[160px] text-xs ${imageReview === 'pending' ? 'text-yellow-700' : 'text-red-600'}`}
                                    role="status"
                                >
                                    {PROFILE_IMAGE_REVIEW_MESSAGES[imageReview]}
                                </p>
                            )}
                        </div>
                        <div className="flex-1">
                            <h1 className="text-3xl font-bold mb-2 text-gray-900">{displayName || user?.username || 'アリス'}</h1>
                            {uploadError && (
                                <p className="text-red-500 text-sm mb-2">{uploadError}</p>
                            )}
                            <div className="flex gap-8 py-4 border-y border-gray-200">
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-pink-500">156</div>
                                    <div className="text-sm text-gray-500 mt-1">フォロワー</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-pink-500">342</div>
                                    <div className="text-sm text-gray-500 mt-1">今月の視聴者</div>
                                </div>
                            </div>
                            <div className="flex gap-4 mt-6">
                                <button
                                    onClick={() => setIsEditingProfile(!isEditingProfile)}
                                    className={`py-3 px-6 rounded-md font-semibold transition-all text-sm ${
                                        isEditingProfile
                                            ? 'bg-pink-500 text-white hover:bg-pink-600'
                                            : 'bg-white border border-gray-300 text-gray-500 hover:bg-gray-50'
                                    }`}
                                >
                                    {isEditingProfile ? '編集を閉じる' : 'プロフィール編集'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Profile Form - 編集モード時のみ表示（プロフィールカード内に統合） */}
                    {isEditingProfile && (
                        <div className="mt-6 pt-6 border-t border-gray-200">
                            <h2 className="text-xl font-bold text-gray-900 mb-6">プロフィール設定</h2>

                            {saveError && (
                                <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-md text-sm">
                                    {saveError}
                                </div>
                            )}
                            {saveSuccess && (
                                <div className="mb-4 p-3 bg-green-100 border border-green-300 text-green-700 rounded-md text-sm">
                                    プロフィールを保存しました
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 items-start">
                                <div className="space-y-2">
                                    <label className="block font-semibold text-gray-700 text-sm text-left">配信者名</label>
                                    <input
                                        type="text"
                                        className="w-full p-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-pink-500 focus:ring-2 focus:ring-pink-100 transition-all"
                                        value={displayName}
                                        onChange={(e) => setDisplayName(e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="block font-semibold text-gray-700 text-sm text-left">年齢</label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="number"
                                            min="0"
                                            max="150"
                                            className="w-full p-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-pink-500 focus:ring-2 focus:ring-pink-100 transition-all text-right"
                                            value={age}
                                            onChange={(e) => setAge(e.target.value)}
                                            placeholder="24"
                                        />
                                        <span className="text-gray-700 text-sm flex-shrink-0">歳</span>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="block font-semibold text-gray-700 text-sm text-left">身長</label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="number"
                                            min="0"
                                            max="300"
                                            className="w-full p-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-pink-500 focus:ring-2 focus:ring-pink-100 transition-all text-right"
                                            value={height}
                                            onChange={(e) => setHeight(e.target.value)}
                                            placeholder="162"
                                        />
                                        <span className="text-gray-700 text-sm flex-shrink-0">cm</span>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                <div className="space-y-2">
                                    <label className="block font-semibold text-gray-700 text-sm text-left">趣味</label>
                                    <input
                                        type="text"
                                        className="w-full p-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-pink-500 focus:ring-2 focus:ring-pink-100 transition-all"
                                        value={hobbies}
                                        onChange={(e) => setHobbies(e.target.value)}
                                        placeholder="例: 読書、映画鑑賞"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="block font-semibold text-gray-700 text-sm text-left">好きな食べ物</label>
                                    <input
                                        type="text"
                                        className="w-full p-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-pink-500 focus:ring-2 focus:ring-pink-100 transition-all"
                                        value={favoriteFood}
                                        onChange={(e) => setFavoriteFood(e.target.value)}
                                        placeholder="例: イタリアン、スイーツ"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="block font-semibold text-gray-700 text-sm text-left">チャームポイント</label>
                                    <input
                                        type="text"
                                        className="w-full p-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-pink-500 focus:ring-2 focus:ring-pink-100 transition-all"
                                        value={features}
                                        onChange={(e) => setFeatures(e.target.value)}
                                        placeholder="例: 笑顔、話しやすさ"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2 mb-6">
                                <label className="block font-semibold text-gray-700 text-sm text-left">自己紹介</label>
                                <textarea
                                    className="w-full p-3 border border-gray-300 rounded-md text-sm min-h-[100px] resize-y focus:outline-none focus:border-pink-500 focus:ring-2 focus:ring-pink-100 transition-all"
                                    value={bio}
                                    onChange={(e) => setBio(e.target.value)}
                                    placeholder="自己紹介を入力してください"
                                />
                            </div>

                            <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
                                <button
                                    onClick={() => setIsEditingProfile(false)}
                                    className="py-3 px-6 bg-white border border-gray-300 text-gray-500 hover:bg-gray-50 rounded-md font-semibold transition-all text-sm"
                                    disabled={isSaving}
                                >
                                    キャンセル
                                </button>
                                <button
                                    onClick={handleSaveProfile}
                                    disabled={isSaving}
                                    className="py-3 px-6 bg-pink-500 hover:bg-pink-600 text-white rounded-md font-semibold transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isSaving ? '保存中...' : '更新する'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* 本人確認セクション - プロフィール関連なのでプロフィールカードの直後に配置 */}
                <div className="bg-white rounded-xl p-8 mt-8 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-bold text-gray-900">{t('ekyc.title', '本人確認')}</h2>
                        <span className={`px-3 py-1.5 rounded-md text-xs font-semibold ${getVerificationStatusDisplay().color}`}>
                            {getVerificationStatusDisplay().text}
                        </span>
                    </div>

                    {isLoadingVerification ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
                        </div>
                    ) : verificationStatus?.is_verified ? (
                        // 確認済み
                        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                            <div className="flex items-center gap-3 mb-3">
                                <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span className="font-semibold text-green-800">{t('ekyc.status.approved', '本人確認が完了しています')}</span>
                            </div>
                            {verificationStatus.verified_at && (
                                <p className="text-sm text-green-700">
                                    確認日: {new Date(verificationStatus.verified_at).toLocaleDateString('ja-JP')}
                                </p>
                            )}
                        </div>
                    ) : verificationStatus?.status && ['SUBMITTED', 'API_VERIFYING'].includes(verificationStatus.status) ? (
                        // GMO本人確認中
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                            <div className="flex items-center gap-3 mb-3">
                                <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span className="font-semibold text-blue-800">{t('ekyc.status.api_verifying', '本人確認中です')}</span>
                            </div>
                            <p className="text-sm text-blue-700">
                                GMO本人確認画面での手続き完了を待っています。スマートフォンで本人確認を完了してください。一定時間が経過すると再申請できます。
                            </p>
                        </div>
                    ) : verificationStatus?.status === 'PENDING_REVIEW' ? (
                        // 管理者審査待ち
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                            <div className="flex items-center gap-3 mb-3">
                                <svg className="w-6 h-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span className="font-semibold text-yellow-800">{t('ekyc.status.pending', '審査中です')}</span>
                            </div>
                            <p className="text-sm text-yellow-700">
                                {t('ekyc.message.pending', '本人確認書類の審査を行っています。しばらくお待ちください。')}
                            </p>
                        </div>
                    ) : verificationStatus?.status === 'EXPIRED' ? (
                        // 期限切れ
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                            <div className="flex items-center gap-3 mb-3">
                                <svg className="w-6 h-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M4.93 19h14.14c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.2 16c-.77 1.33.19 3 1.73 3z" />
                                </svg>
                                <span className="font-semibold text-yellow-800">{t('ekyc.status.expired', '本人確認がタイムアウトしました')}</span>
                            </div>
                            <p className="text-sm text-yellow-700 mb-4">
                                スマートフォンから再度本人確認を行ってください。
                            </p>
                            <button
                                onClick={() => setIsEKYCModalOpen(true)}
                                className="py-2 px-4 bg-pink-500 hover:bg-pink-600 text-white rounded-md font-semibold transition-all text-sm"
                            >
                                {t('ekyc.button.retry', '再度確認する')}
                            </button>
                        </div>
                    ) : verificationStatus?.status === 'REJECTED' ? (
                        // 却下
                        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                            <div className="flex items-center gap-3 mb-3">
                                <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span className="font-semibold text-red-800">{t('ekyc.status.rejected', '本人確認が却下されました')}</span>
                            </div>
                            {verificationStatus?.rejection_reason && (
                                <div className="bg-red-100 border border-red-300 rounded p-3 mb-4">
                                    <p className="text-sm text-red-800 font-medium">却下理由:</p>
                                    <p className="text-sm text-red-700">{verificationStatus.rejection_reason}</p>
                                </div>
                            )}
                            <p className="text-sm text-red-700 mb-4">
                                {t('ekyc.message.rejected', '再度本人確認を行ってください。')}
                            </p>
                            <button
                                onClick={() => setIsEKYCModalOpen(true)}
                                className="py-2 px-4 bg-pink-500 hover:bg-pink-600 text-white rounded-md font-semibold transition-all text-sm"
                            >
                                {t('ekyc.button.retry', '再度確認する')}
                            </button>
                        </div>
                    ) : (
                        // 未確認
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                            <div className="flex items-center gap-3 mb-3">
                                <svg className="w-6 h-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                                <span className="font-semibold text-gray-700">{t('ekyc.status.unverified', '本人確認が必要です')}</span>
                            </div>
                            <p className="text-sm text-gray-600 mb-4">
                                {t('ekyc.message.unverified', '配信を開始するには本人確認が必要です。運転免許証またはマイナンバーカードをご用意ください。')}
                            </p>
                            <button
                                onClick={() => setIsEKYCModalOpen(true)}
                                className="py-2 px-4 bg-pink-500 hover:bg-pink-600 text-white rounded-md font-semibold transition-all text-sm"
                            >
                                {t('ekyc.button.start', '本人確認を始める')}
                            </button>
                        </div>
                    )}
                </div>

                {/* Schedule Section */}
                <div className="bg-white rounded-xl p-8 mt-8 shadow-sm">
                    <h2 className="text-xl font-bold text-gray-900 mb-6">今後の配信予定</h2>

                    {isLoadingSchedules ? (
                        <div className="space-y-4">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="flex gap-6 p-6 border border-gray-200 rounded-lg items-center animate-pulse">
                                    <div className="w-[60px] h-[60px] rounded-lg bg-gray-200 flex-shrink-0" />
                                    <div className="flex-1 space-y-2">
                                        <div className="h-4 bg-gray-200 rounded w-1/3" />
                                        <div className="h-4 bg-gray-200 rounded w-1/2" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : schedules.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                            <p>配信予定がありません</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {schedules.map((schedule, index) => {
                                const timeUntilStart = getTimeUntilStart(schedule.scheduled_start);
                                const imageUrl = getImageUrlWithCacheBuster(profileImageUrl);
                                return (
                                    <div
                                        key={schedule.id}
                                        className={`flex gap-6 p-6 border border-gray-200 rounded-lg items-center ${
                                            index < schedules.length - 1 ? 'mb-4' : ''
                                        }`}
                                    >
                                        <div className="w-[60px] h-[60px] rounded-lg overflow-hidden flex-shrink-0 bg-gray-200">
                                            {imageUrl ? (
                                                <img
                                                    src={imageUrl}
                                                    alt={displayName || user?.username || '配信者'}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center bg-gray-200">
                                                    <DefaultUserIcon className="w-8 h-8 text-gray-400" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-1">
                                            <div className="font-bold text-gray-900 mb-1">
                                                {formatScheduleDate(schedule.scheduled_start, schedule.scheduled_end)}
                                            </div>
                                            <div className="font-semibold text-gray-700 mb-1">
                                                {displayName || user?.username || '配信者'}の配信
                                            </div>
                                            <div className="text-sm text-gray-500">
                                                ステータス: {schedule.status === 'scheduled' ? '予定' : schedule.status === 'live' ? '配信中' : schedule.status}
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-3 items-end">
                                            {timeUntilStart && (
                                                <span className="px-3 py-1.5 rounded-md text-xs font-semibold bg-yellow-100 text-yellow-800">
                                                    {timeUntilStart}
                                                </span>
                                            )}
                                            <Link
                                                to={ROUTES.PUBLISH_OME}
                                                className="py-2 px-4 bg-pink-500 hover:bg-pink-600 text-white rounded-md font-semibold transition-all text-sm"
                                            >
                                                配信画面へ
                                            </Link>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* eKYC モーダル */}
                {user?.id && (
                    <EKYCModal
                        isOpen={isEKYCModalOpen}
                        onClose={() => setIsEKYCModalOpen(false)}
                        userId={user.id}
                        onVerificationComplete={handleVerificationComplete}
                    />
                )}
            </div>
        </div>
    );
}
